import { useLocalization } from "@fluent/react";
import { Button, ContentCard } from "@narmi/design_system";
import * as Sentry from "@sentry/react";
import { BulletPoint } from "@lending/components/BulletPoint/BulletPoint";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useNavigateWithSearch } from "@lending/hooks/useNavigateWithSearch";
import styles from "./Activate.module.css";
import { BankBuildingIcon } from "./BankBuildingIcon";
import { useCreateMembershipAccountApplication } from "./hooks/useCreateMembershipAccountApplication";
import { useMembershipShareApplication } from "../hooks/useMembershipShareApplication";
import { useMembershipShareProduct } from "./hooks/useMembershipShareProduct";

function Activate() {
  const { l10n } = useLocalization();
  const navigate = useNavigateWithSearch();
  const { institution } = useGetInstitutionQuery();
  const { isLoading: isApplicationLoading, isRefetching: isApplicationRefetching } =
    useMembershipShareApplication();
  const { membershipShareProduct } = useMembershipShareProduct();
  const { createMembershipAccountApplication, isPending } = useCreateMembershipAccountApplication();

  if (!membershipShareProduct) {
    Sentry.captureMessage(
      "Membership share product not found. The membership_share_product_for_lending setting may reference a product that does not exist.",
      { level: "error" }
    );
    return <ErrorScreen />;
  }

  const handleContinue = async () => {
    if (isApplicationLoading || isApplicationRefetching) {
      return;
    }

    const result = await createMembershipAccountApplication();

    if (result.status === "success") {
      void navigate("../disclosures");
      return;
    }

    if (result.status === "authentication_required") {
      void navigate("../verify-email");
      return;
    }

    if (result.status === "email_conflict") {
      void navigate("../use-different-email");
    }
  };

  return (
    <PageLayout className={styles.pageLayout}>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.header}>
            <h1 className="margin--bottom--xs">
              {l10n.getString(
                "membership-share-activate-heading",
                null,
                "Activate your membership"
              )}
            </h1>
            <div className={styles.subheading}>
              {l10n.getString(
                "membership-share-activate-subheading",
                { institutionName: institution.long_name },
                "Open a free membership account with {$institutionName}. This is required for us to process your loan application and distribute your funds."
              )}
            </div>
          </div>

          <ContentCard radiusSize="l" kind="bordered" paddingSize="none">
            <div className={styles.productCard}>
              <div className={styles.productCardLeft}>
                <BankBuildingIcon className={styles.bankIcon} />
              </div>
              <div className={styles.productCardRight}>
                <div className={styles.productName}>{membershipShareProduct.name}</div>
                <div className={styles.bulletPoints}>
                  {membershipShareProduct.details?.map((detail: string, index: number) => (
                    <BulletPoint key={index} text={detail} className="fontSize--s" />
                  ))}
                </div>
                <div className={styles.infoLink}>
                  <Button
                    as="a"
                    kind="plain"
                    label={l10n.getString("ui-info-disclaimer", null, "Info & disclaimer")}
                    size="xs"
                    onClick={() => {
                      window.open(
                        membershipShareProduct.more_details_url ?? "#",
                        "_blank",
                        "noopener, noreferrer"
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </ContentCard>

          <div className={styles.buttonContainer}>
            <Button
              label={l10n.getString("button-label-continue", null, "Continue")}
              onClick={() => void handleContinue()}
              isLoading={isPending || isApplicationRefetching}
              disabled={isApplicationLoading || isApplicationRefetching}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

export { Activate };
